import {
  Badge,
  BadgeVariantEnum,
  Button,
  ButtonColorEnum,
  ChevronDownIcon,
  ChevronUpIcon,
} from '@acceso/design-system';
import clsx from 'clsx';
import { useRef, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

import Listbox, { ListboxSelected } from '@/components/Listbox';
import { TableRow } from '@/components/TableRow';
import { VISIBILITY_OPTIONS } from '@/constants/visibility';
import { useBroadcastScheduleContext } from '@/features/broadcast-schedule-program/components/BroadcastScheduleProgramBody/components/BroadcastScheduleProgramBodySegmentToolkit/components/BroadcastScheduleProgramBodySegmentToolkitLogotipos/context/BroadcastScheduleProvider';
import { buildNewPresenceDataToSave } from '@/features/broadcast-schedule-program/components/BroadcastScheduleProgramBody/components/BroadcastScheduleProgramBodySegmentToolkit/components/BroadcastScheduleProgramBodySegmentToolkitLogotipos/helpers/batchSelectionHelpers';
import {
  INLINE_DIALOG_OPTIONS,
  PostPresenceFor,
  TBroadcastScheduleOnUpdateInline,
} from '@/features/broadcast-schedule-program/components/BroadcastScheduleProgramBody/components/BroadcastScheduleProgramBodySegmentToolkit/components/BroadcastScheduleProgramBodySegmentToolkitLogotipos/types';
import { useBroadcastScheduleProgram } from '@/features/broadcast-schedule-program/context/BroadcastScheduleProgramContext';
import { getDurationInSecondsBetweenStartDateAndEndDate } from '@/helpers/format-date';
import { TPresenceItem } from '@/types/presences';
import { EVisibility } from '@/types/types';
import { NumberIntoHHMMSS } from '@/utils/hh-mm-ss-into-number';

type VisibilityInlineDropdownProps<T extends TPresenceItem> = {
  item: T;
  onUpdateInline?: TBroadcastScheduleOnUpdateInline<PostPresenceFor<T>>;
  onPreview?: (item: T) => void;
  buildDataToSave?: (item: T) => PostPresenceFor<T>;
  presenceData?: { id?: string };
};

export function VisibilityInlineDropdown<T extends TPresenceItem>({
  item,
  onUpdateInline,
  onPreview,
  buildDataToSave,
  presenceData,
}: VisibilityInlineDropdownProps<T>) {
  const { logoData, playerVideoCurrentTime } = useBroadcastScheduleProgram();
  const { openInline, toggleInline, dropdownCoords, setDropdownCoordsFromRect } =
    useBroadcastScheduleContext();
  const { id: itemId } = item;
  const { t } = useTranslation();

  const activeData = presenceData ?? logoData;
  const dataToSave = (buildDataToSave ?? buildNewPresenceDataToSave) as (
    item: T
  ) => PostPresenceFor<T>;

  const btnRef = useRef<HTMLButtonElement | null>(null);

  const isOpen = openInline.visibility === itemId;

  useLayoutEffect(() => {
    if (isOpen && btnRef.current) {
      if (activeData?.id !== item.id) {
        onPreview?.(item);
      }

      setDropdownCoordsFromRect(btnRef.current.getBoundingClientRect());
    }
  }, [isOpen]);

  if (!itemId) return <></>;

  const totalSeconds = getDurationInSecondsBetweenStartDateAndEndDate(
    new Date(item.start_datetime),
    new Date(item.end_datetime)
  );

  const renderDuration = (totalSeconds: number) => {
    if (totalSeconds > 0) return NumberIntoHHMMSS(totalSeconds);
    if (totalSeconds === 0) return NumberIntoHHMMSS(1);

    return <Badge variant={BadgeVariantEnum.PrimaryFilled}>{t('Inválida')}</Badge>;
  };

  const renderedDate = renderDuration(totalSeconds);

  const updateVisibility = (value: ListboxSelected) => {
    const visibilitySelected = value[0] as EVisibility;
    const newData = dataToSave(item);
    const applySilently = false;

    (newData.parameters as any).visibility = visibilitySelected;
    onUpdateInline?.(newData, applySilently, playerVideoCurrentTime);
    toggleInline(INLINE_DIALOG_OPTIONS.Visibility, itemId);
  };

  return (
    <div className="relative">
      <Button
        ref={btnRef}
        color={ButtonColorEnum.SECONDARY_OLD}
        className={clsx(
          'h-auto cursor-pointer',
          openInline.visibility === itemId ? 'bg-neutral-20' : 'bg-transparent'
        )}
        onClick={() => {
          toggleInline(INLINE_DIALOG_OPTIONS.Visibility, itemId);
        }}
        rightIcon={() =>
          openInline.visibility === itemId ? <ChevronUpIcon /> : <ChevronDownIcon />
        }
      >
        <TableRow
          title={(item.parameters as any)?.visibility}
          content={renderedDate}
          className="flex flex-col"
        />
      </Button>
      {isOpen &&
        dropdownCoords &&
        createPortal(
          <div
            className="absolute z-100 mt-2 min-w-[300px] border border-neutral-20 bg-white p-4 shadow-lg"
            style={{
              top: dropdownCoords.top,
              left: dropdownCoords.left,
            }}
          >
            <Listbox
              dark={false}
              mode="single"
              items={VISIBILITY_OPTIONS}
              onChange={updateVisibility}
              identifiers={{
                key: 'value',
                value: 'value',
                label: 'label',
              }}
              selected={undefined}
            />
          </div>,
          document.body
        )}
    </div>
  );
}
